import React, { useCallback, useEffect, useState } from 'react';
import { useMaterialDetailModal } from '../../../../contexts/MaterialDetailModalContext.tsx';
import { isAxiosError } from 'axios';
import { getCommerceMaterials } from '../../../../services/commerceService.ts';
import type { UserCommerceRef } from '../../../../utils/userCommerces.ts';
import CommerceMaterialsCatalog from '../MisMaterialesComercio/CommerceMaterialsCatalog.tsx';
import {
  type FlatMaterialRow,
  extractCommerceMaterialsDataArray,
  flattenCategories,
  parseCommerceMaterialsPayload,
} from '../MisMaterialesComercio/commerceMaterialsModel.ts';
import styles from './ProfileCommerceMaterialsPublic.module.scss';

interface ProfileCommerceMaterialsPublicProps {
  commerces: UserCommerceRef[];
}

const ProfileCommerceMaterialsPublic: React.FC<ProfileCommerceMaterialsPublicProps> = ({
  commerces,
}) => {
  const { openMaterialDetail } = useMaterialDetailModal();
  const [commerceId, setCommerceId] = useState<number | null>(() => commerces[0]?.id ?? null);
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [warn, setWarn] = useState<string | null>(null);
  const [flatRows, setFlatRows] = useState<FlatMaterialRow[]>([]);

  useEffect(() => {
    if (commerces.length === 0) return;
    setCommerceId(commerces[0].id);
  }, [commerces]);

  const load = useCallback(async (id: number) => {
    setLoading(true);
    setBlocked(false);
    setWarn(null);
    try {
      const res = await getCommerceMaterials(id);
      const raw = extractCommerceMaterialsDataArray(res.data);
      const categories = parseCommerceMaterialsPayload(raw, id);
      setFlatRows(flattenCategories(categories));
    } catch (e: unknown) {
      const status = isAxiosError(e) ? e.response?.status : null;
      if (status === 401 || status === 403) {
        setBlocked(true);
      } else {
        setWarn('No pudimos cargar el catálogo en este momento.');
      }
      setFlatRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (commerceId == null) return;
    void load(commerceId);
  }, [commerceId, load]);

  if (commerces.length === 0) return null;

  const singleName = commerces[0]?.name?.trim() ?? '';

  return (
    <div className={styles.wrap}>
      <h4 className={styles.title}>Materiales</h4>
      {singleName ? <p className={styles.commerceLead}>{singleName}</p> : null}
      <p className={styles.intro}>
        Listado de referencia en este perfil; precios y condiciones, consultá directamente al comercio.
      </p>
      {loading ? <p className={styles.meta}>Cargando catálogo…</p> : null}
      {blocked && !loading ? (
        <p className={styles.notice}>
          El catálogo no está disponible de forma anónima. Iniciá sesión o contactá al comercio para cotizar.
        </p>
      ) : null}
      {warn && !loading && !blocked ? <p className={styles.notice}>{warn}</p> : null}
      {!loading && !blocked ? (
        <CommerceMaterialsCatalog
          mode="public"
          flatRows={flatRows}
          onPublicMaterialDetailClick={(id) => void openMaterialDetail(id)}
        />
      ) : null}
    </div>
  );
};

export default ProfileCommerceMaterialsPublic;
